import { apiClient } from './api';

export interface OTAVersion {
  id: number;
  version: string;
  filename: string;
  file_size: number;
  sha256: string;
  sha256_signature?: string;
  changelog?: string;
  release_notes?: string;
  is_stable: boolean;
  is_beta: boolean;
  is_deprecated: boolean;
  download_count: number;
  success_count: number;
  failure_count: number;
  uploaded_by?: string;
  uploaded_at: string;
  released_at?: string;
  created_at: string;
}

export interface OTAUpdate {
  id: number;
  device_id: string;
  version_id: number;
  from_version?: string;
  to_version: string;
  status: 'PENDING' | 'DOWNLOADING' | 'INSTALLING' | 'SUCCESS' | 'FAILED' | 'ROLLED_BACK';
  started_at: string;
  completed_at?: string;
  failure_reason?: string;
  retry_count: number;
}

export interface VersionsResponse {
  data: OTAVersion[];
  total: number;
}

export const otaService = {
  getVersions: async (limit = 20, offset = 0): Promise<VersionsResponse> => {
    return apiClient.get<VersionsResponse>('/ota/versions', {
      params: { limit, offset },
    });
  },

  uploadFirmware: async (
    file: File,
    version: string,
    changelog: string,
    isStable: boolean,
    isBeta: boolean
  ): Promise<OTAVersion> => {
    const formData = new FormData();
    formData.append('firmware', file);
    formData.append('version', version);
    formData.append('changelog', changelog);
    formData.append('is_stable', String(isStable));
    formData.append('is_beta', String(isBeta));

    return apiClient.post<OTAVersion>('/ota/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  triggerUpdate: async (deviceId: string, version: string): Promise<OTAUpdate> => {
    return apiClient.post<OTAUpdate>('/ota/trigger', { deviceId, version });
  },

  checkForUpdate: async (deviceId: string, currentVersion: string): Promise<OTACheckResponse> => {
    return apiClient.get<OTACheckResponse>(
      '/ota/check',
      { params: { device_id: deviceId, version: currentVersion } }
    );
  },

  checkForBetaUpdate: async (deviceId: string, currentVersion: string): Promise<OTACheckResponse> => {
    return apiClient.get<OTACheckResponse>(
      '/ota/check',
      { params: { device_id: deviceId, version: currentVersion, channel: 'beta' } }
    );
  },
};

export interface OTACheckResponse {
  update_available: boolean;
  latest_version?: string;
  download_url?: string;
  file_size?: number;
  sha256?: string;
  changelog?: string;
  is_stable?: boolean;
  is_beta?: boolean;
}
